/** @jsxImportSource @emotion/react */

import { FC, useState } from 'react'
import { Form as FinalForm } from 'react-final-form'
import arrayMutators from 'final-form-arrays'
import { FieldArray } from 'react-final-form-arrays'
import { Button } from '@mui/material'
import { useMutation } from '@apollo/client'
import { FaqPages, FaqTypeEnum, LinkFaqInput } from '__generatedTypes__/globalTypes'
import { BulkCreateUpdateFaq, BulkCreateUpdateFaqVariables } from '__generatedTypes__/BulkCreateUpdateFaq'
import { BULK_CREATE_UPDATE_FAQ, BULK_REMOVE_FAQ } from 'queries/faq'
import { BulkRemoveFaq, BulkRemoveFaqVariables } from '__generatedTypes__/BulkRemoveFaq'
import { usePlatformsAndSubjects } from 'hooks/usePlatformsAndSubjects'
import { toast } from 'utils/toast'
import { FaqListItem } from './types'
import { validateFaqList } from './validation'
import * as classes from './styles'
import { FAQFieldArray } from './FAQFieldArray'

type FormValues = {
  list: FaqListItem[]
}

type Props = {
  page: FaqPages
  allowedTypes: FaqTypeEnum[]
  initialValues: FormValues
  refetch: () => void
}

export const Form: FC<Props> = ({ page, allowedTypes, initialValues, refetch }) => {
  const { selectedPlatform } = usePlatformsAndSubjects({})

  const [idsToDelete, setIdsToDelete] = useState<LinkFaqInput[]>([])
  const [bulkCreateUpdateFaq] = useMutation<BulkCreateUpdateFaq, BulkCreateUpdateFaqVariables>(BULK_CREATE_UPDATE_FAQ)
  const [bulkRemoveFaq] = useMutation<BulkRemoveFaq, BulkRemoveFaqVariables>(BULK_REMOVE_FAQ)

  const removeFaqByIds = async () => {
    if (idsToDelete?.length) {
      try {
        await bulkRemoveFaq({
          variables: {
            ids: idsToDelete,
          },
        })
        setIdsToDelete([])
      } catch (error) {
        return Promise.reject(error)
      }
    }
    return
  }

  const handleSubmit = async (values: FormValues) => {
    try {
      if (!selectedPlatform) return

      if (values.list?.length) {
        await bulkCreateUpdateFaq({
          variables: {
            createFaqInput: values.list.map(({ entityId, answerAsHtml, question, type }, order) => ({
              id: entityId,
              answerAsHtml,
              page,
              question,
              order,
              type: type?.id ?? FaqTypeEnum.Standard,
              platform: { id: selectedPlatform.id },
            })),
          },
        })
      }

      await removeFaqByIds()
      refetch()
      toast.success('FAQ updated successfully')
    } catch (error) {
      toast.error('Failed to update FAQ')
    }
  }

  return (
    <FinalForm<FormValues>
      mutators={{ ...arrayMutators }}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      render={({ form, submitting, handleSubmit }) => (
        <form onSubmit={handleSubmit}>
          <FieldArray name="list" validate={validateFaqList}>
            {({ fields, meta }) => (
              <FAQFieldArray
                fields={fields}
                meta={meta}
                allowedTypes={allowedTypes}
                onDelete={(id) => setIdsToDelete((ids) => [...ids, { id }])}
              />
            )}
          </FieldArray>
          <div css={classes.actionButtonsWrapper}>
            <Button
              color="blue"
              variant="outlined"
              disabled={submitting}
              css={classes.actionButton}
              onClick={() => {
                form.reset(initialValues)
              }}
            >
              Cancel
            </Button>
            <Button color="blue" type="submit" disabled={submitting} css={classes.actionButton}>
              Update
            </Button>
          </div>
        </form>
      )}
    />
  )
}
